import makeApiCall from "./master/axiosIndex";

export interface ThreeMConsolidationFilters {
  phaseId?: number | string;
  activityId?: number | string;
  taskId?: number | string;
  search?: string;
}

export const threeMReportApis = {
  // Project-level 3M (Material / Manpower / Machinery) consolidation rollup —
  // phases → activities → tasks, each carrying a ThreeMRollup. Only non-empty
  // filters are sent as query params.
  get3MConsolidation: (
    projectId: number | string,
    filters: ThreeMConsolidationFilters = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "taskId", "search"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/${projectId}`,
      method: "get",
      params,
    });
  },
  // Project-level material cost rollup: distinct materials aggregated across
  // task BOQ lines (qty, avg rate, total cost), sorted by totalCost DESC.
  get3MMaterialSummary: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/material-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // Line-level detail for one task: material BOQ lines (with norm provenance),
  // manpower resources and machinery/tools (with consumables).
  get3MTaskDetail: (taskId: number | string): Promise<any> => {
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/task/${taskId}`,
      method: "get",
    });
  },
  // Per-material phase → activity → task cost breakdown (Material Cost
  // Analysis drill-down). Returns flat rows { phaseId, phaseName, activityId,
  // activityName, taskId, taskName, totalQuantity, totalCost }; UI nests them.
  get3MMaterialTaskBreakdown: (
    projectId: number | string,
    materialId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/material-breakdown/${projectId}/${materialId}`,
      method: "get",
      params,
    });
  },
  // Task-grain (task, month) money cells for the 3M Consolidation Time Phasing
  // tab. MUST be called with the SAME filters as get3MConsolidation — the
  // client rolls these up over that tree, so mismatched filters are nonsense.
  get3MTimePhasing: (
    projectId: number | string,
    filters: ThreeMConsolidationFilters = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "taskId", "search"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/time-phasing/${projectId}`,
      method: "get",
      params,
    });
  },
  // Per-material month-wise schedule (Material Cost Analysis monthly drawer):
  // { months: [{ month, monthQuantity, monthCost, taskCount }], coverage }.
  // Months come from task_schedules scaled by each task's monthly share, so
  // Sum(months) can fall short of the register total — `coverage` says why.
  get3MMaterialMonthlySchedule: (
    projectId: number | string,
    materialId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/material-monthly/${projectId}/${materialId}`,
      method: "get",
      params,
    });
  },
  // All-materials month grid for the Excel "Month-wise Register" sheet: flat
  // { materialId, month, monthQuantity, monthCost } cells; the export pivots
  // them into month columns against the material-summary rows it already has.
  get3MMaterialMonthlyScheduleAll: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/material-monthly-all/${projectId}`,
      method: "get",
      params,
    });
  },
  // Per-item month-wise schedule for Manpower / Machinery (monthly drawers).
  // Identity is composite and travels as query params; ids are stripped when
  // absent because an absent id means IS NULL server-side, which is what keeps
  // the "Unlinked labour" / "Unlinked tool" rows drillable.
  get3MManpowerMonthlySchedule: (
    projectId: number | string,
    identity: {
      source: "PRW" | "OTHERS";
      manpowerNormId?: number;
      laborTypeId?: number;
      laborCategoryId?: number;
      workUomId?: number;
      phaseId?: number | string;
      activityId?: number | string;
    }
  ): Promise<any> => {
    const params: Record<string, any> = { source: identity.source };
    (
      [
        "manpowerNormId",
        "laborTypeId",
        "laborCategoryId",
        "workUomId",
        "phaseId",
        "activityId",
      ] as const
    ).forEach((key) => {
      const value = identity[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/manpower-monthly/${projectId}`,
      method: "get",
      params,
    });
  },
  get3MMachineryMonthlySchedule: (
    projectId: number | string,
    identity: {
      source: "NORM" | "TOOLS" | "EQUIPMENT";
      machineryNormId?: number;
      toolId?: number;
      workUomId?: number;
      /** EQUIPMENT identity (deployment norm); ignored by older backends. */
      deploymentNormId?: number;
      phaseId?: number | string;
      activityId?: number | string;
    }
  ): Promise<any> => {
    const params: Record<string, any> = { source: identity.source };
    (
      [
        "machineryNormId",
        "toolId",
        "workUomId",
        "deploymentNormId",
        "phaseId",
        "activityId",
      ] as const
    ).forEach((key) => {
      const value = identity[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-monthly/${projectId}`,
      method: "get",
      params,
    });
  },
  // All-items month grids for the Excel "Month-wise Register" sheets. Rows
  // carry `head` (manpower: the source; machinery: the norm category or
  // 'TOOLS') so the sheet can band items into head sections.
  get3MManpowerMonthlyScheduleAll: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/manpower-monthly-all/${projectId}`,
      method: "get",
      params,
    });
  },
  get3MMachineryMonthlyScheduleAll: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> & {
      head?: string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "head"] as const).forEach((key) => {
      const value = (filters as any)[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-monthly-all/${projectId}`,
      method: "get",
      params,
    });
  },
  // Labour-item-wise cost rollup (Manpower Cost Analysis): dual-source union
  // of applied PRW manpower norms + task resources, sorted by totalCost DESC.
  get3MManpowerSummary: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/manpower-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // Per-labour-item phase → activity → task breakdown (Manpower Cost Analysis
  // drill-down). Identity = source + conditional ids as query params — an
  // absent OTHERS id means IS NULL on the backend (drillable "Unlinked labour").
  get3MManpowerTaskBreakdown: (
    projectId: number | string,
    query: {
      source: "PRW" | "OTHERS";
      manpowerNormId?: number;
      laborTypeId?: number;
      laborCategoryId?: number;
      workUomId?: number;
      phaseId?: number | string;
      activityId?: number | string;
    }
  ): Promise<any> => {
    const params: Record<string, any> = { source: query.source };
    (
      ["manpowerNormId", "laborTypeId", "laborCategoryId", "workUomId"] as const
    ).forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null && Number(value) > 0) {
        params[key] = value;
      }
    });
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/manpower-breakdown/${projectId}`,
      method: "get",
      params,
    });
  },
  // ALL materials' phase → activity → task rows in one call (Excel export).
  get3MMaterialTaskBreakdownAll: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/material-breakdown-all/${projectId}`,
      method: "get",
      params,
    });
  },
  // Machinery-item-wise cost rollup (Machinery Cost Analysis): dual-source
  // union of applied machinery norms + task tools (plus the non-itemized
  // consumables bucket in the totals), sorted by totalCost DESC. `head`
  // filters to one machinery head (category) or 'TOOLS'; omitted = all.
  // 3M Deviation Summary: per-resource BOQ vs target vs actual rate for all
  // three Ms in one call (the page's three tabs share one filter set).
  get3MDeviationSummary: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      taskId?: number | string;
      tab?: string;
      head?: string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "taskId", "tab", "head"] as const).forEach(
      (key) => {
        const value = filters[key];
        if (value !== undefined && value !== null && `${value}`.trim() !== "") {
          params[key] = value;
        }
      }
    );
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/deviation-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  get3MMachinerySummary: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      head?: string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "head"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // Server-driven activity-grouped machinery register (Machinery Cost
  // Analysis table): paginated activity groups (each with its item rows
  // nested, cost DESC; activityId 0 = the Tools pseudo-group), searched by
  // activity name and sorted by group total cost. `page` is 1-based,
  // `pageSize` defaults to 10 server-side, `sortDir` defaults to 'desc'.
  get3MMachineryActivitySummary: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      head?: string;
      search?: string;
      sortDir?: "asc" | "desc";
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (
      [
        "phaseId",
        "activityId",
        "head",
        "search",
        "sortDir",
        "page",
        "pageSize",
      ] as const
    ).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-activity-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // Activity → TASK machinery register: same filters as the norm-wise one
  // above, but each activity expands into its TASKS with every machinery
  // source (Mucking + Tools + Equipment) folded into each row. Groups by the
  // TASK's activity, so equipment norms — which carry no activity of their
  // own — appear under real activities instead of one "no activity" bucket.
  get3MMachineryActivityTasks: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      head?: string;
      search?: string;
      sortDir?: "asc" | "desc";
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (
      [
        "phaseId",
        "activityId",
        "head",
        "search",
        "sortDir",
        "page",
        "pageSize",
      ] as const
    ).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-activity-tasks/${projectId}`,
      method: "get",
      params,
    });
  },
  // What an Equipment figure is MADE OF, for whichever scope was clicked on
  // the activity→task register: by machine, by norm and by cost head. Pass
  // taskId for a task row, activityId for an activity row.
  get3MEquipmentDrill: (
    projectId: number | string,
    query: {
      phaseId?: number;
      activityId?: number;
      taskId?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "taskId"] as const).forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/equipment-drill/${projectId}`,
      method: "get",
      params,
    });
  },
  // Per-machinery-item phase → activity → task breakdown (Machinery Cost
  // Analysis drill-down). Identity = source + conditional ids as query params
  // — an absent TOOLS id means IS NULL on the backend (drillable "Unlinked
  // tool").
  get3MMachineryTaskBreakdown: (
    projectId: number | string,
    query: {
      source: "NORM" | "TOOLS" | "EQUIPMENT";
      machineryNormId?: number;
      toolId?: number;
      workUomId?: number;
      /** EQUIPMENT identity (deployment norm); ignored by older backends. */
      deploymentNormId?: number;
      phaseId?: number | string;
      activityId?: number | string;
    }
  ): Promise<any> => {
    const params: Record<string, any> = { source: query.source };
    (
      ["machineryNormId", "toolId", "workUomId", "deploymentNormId"] as const
    ).forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null && Number(value) > 0) {
        params[key] = value;
      }
    });
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = query[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-breakdown/${projectId}`,
      method: "get",
      params,
    });
  },
  // ALL machinery items' phase → activity → task rows in one call (Excel
  // export). Rows carry the summary identity (source + ids, 0 = NULL) for
  // client joins.
  get3MMachineryTaskBreakdownAll: (
    projectId: number | string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      head?: string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId", "head"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/machinery-breakdown-all/${projectId}`,
      method: "get",
      params,
    });
  },
  // Internal-task-number-wise planned vs executed rollup (Internal Task
  // Number Summary report): tasks grouped by internal task number + UOM (a
  // number whose tasks mix UOMs arrives once per UOM), each group carrying
  // its embedded task rows — one fetch powers the table AND the Excel export.
  getInternalTaskSummary: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/internal-task-summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // Monthly Budget P&L (Top Sheet): month-wise inflow/outflow/margin from task
  // schedules × rates & 3M sources. fromMonth/toMonth = FY window (YYYY-MM-01).
  getMonthlyPnlTopSheet: (
    projectId: number | string,
    filters: {
      fromMonth?: string;
      toMonth?: string;
      phaseId?: number | string;
      activityId?: number | string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["fromMonth", "toMonth", "phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-pnl/top-sheet/${projectId}`,
      method: "get",
      params,
    });
  },
  // One month's item-level breakdown (Materials / Manpower / Machinery tabs).
  getMonthlyPnlMonthDetail: (
    projectId: number | string,
    month: string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = { month };
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-pnl/month-detail/${projectId}`,
      method: "get",
      params,
    });
  },
  // One month's task rows — server-paginated (page 1-based, pageSize default 50).
  getMonthlyPnlMonthTasks: (
    projectId: number | string,
    month: string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = { month };
    (["phaseId", "activityId", "page", "pageSize"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-pnl/month-tasks/${projectId}`,
      method: "get",
      params,
    });
  },
  // Monthly Execution Summary: client plan vs target vs actual per month +
  // carry-over waterfall + 3M planned-vs-earned pillars.
  getMonthlyExecutionSummary: (
    projectId: number | string,
    filters: {
      fromMonth?: string;
      toMonth?: string;
      phaseId?: number | string;
      activityId?: number | string;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["fromMonth", "toMonth", "phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-execution/summary/${projectId}`,
      method: "get",
      params,
    });
  },
  // One execution month's item tabs (planned vs earned per item + deviation).
  getMonthlyExecutionMonthDetail: (
    projectId: number | string,
    month: string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = { month };
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-execution/month-detail/${projectId}`,
      method: "get",
      params,
    });
  },
  // Actual Client Billing drill: activity-wise running meters + task/day rows.
  getMonthlyExecutionBillingDrill: (
    projectId: number | string,
    month: string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = { month };
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-execution/billing-drill/${projectId}`,
      method: "get",
      params,
    });
  },
  // One execution month's task rows — server-paginated (schedule∪actual spine).
  getMonthlyExecutionMonthTasks: (
    projectId: number | string,
    month: string,
    filters: {
      phaseId?: number | string;
      activityId?: number | string;
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<any> => {
    const params: Record<string, any> = { month };
    (["phaseId", "activityId", "page", "pageSize"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/monthly-execution/month-tasks/${projectId}`,
      method: "get",
      params,
    });
  },
  // ALL labour items' phase → activity → task rows in one call (Excel export).
  // Rows carry the summary identity (source + ids, 0 = NULL) for client joins.
  get3MManpowerTaskBreakdownAll: (
    projectId: number | string,
    filters: Pick<ThreeMConsolidationFilters, "phaseId" | "activityId"> = {}
  ): Promise<any> => {
    const params: Record<string, any> = {};
    (["phaseId", "activityId"] as const).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== "") {
        params[key] = value;
      }
    });
    return makeApiCall({
      url: `/pms-reports/3m-consolidation/manpower-breakdown-all/${projectId}`,
      method: "get",
      params,
    });
  },
};
